<template>
  <div class="home">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="container">
        <div class="hero-content">
          <h1 class="hero-title">{{ $t('home.heroTitle') }}</h1>
          <p class="hero-subtitle">{{ $t('home.heroSubtitle') }}</p>
          <div class="hero-actions">
            <router-link to="/receive-code" class="btn btn-primary">{{ $t('home.startReceiving') }}</router-link>
            <a href="#how-it-works" class="btn btn-outline">{{ $t('home.learnMore') }}</a>
          </div>
        </div>
        <div class="hero-image">
          <img src="@/assets/images/hero-image.svg" :alt="$t('home.platformAlt')">
        </div>
      </div>
    </section>

    <!-- Black Friday Activity Section -->
    <section class="black-friday-section">
      <div class="container">
        <div class="black-friday-card">
          <div class="black-friday-main">
            <div class="black-friday-badge">{{ $t('home.blackFriday.badge') }}</div>
            <h2 class="black-friday-title">{{ $t('home.blackFriday.title') }}</h2>
            <p class="black-friday-subtitle">{{ $t('home.blackFriday.subtitle') }}</p>
            <div class="black-friday-highlights">
              <span>{{ $t('home.blackFriday.highlights.auto') }}</span>
              <span>{{ $t('home.blackFriday.highlights.channel') }}</span>
              <span>{{ $t('home.blackFriday.highlights.record') }}</span>
            </div>
          </div>
          <div class="black-friday-offer">
            <div class="offer-label">{{ $t('home.blackFriday.priceLabel') }}</div>
            <div class="offer-price-row">
              <span class="offer-price">{{ $t('home.blackFriday.priceText') }}</span>
              <span class="offer-original">{{ $t('home.blackFriday.originalLabel') }}</span>
            </div>
            <router-link to="/receive-code" class="black-friday-cta">{{ $t('home.blackFriday.cta') }}</router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="section features-section">
      <div class="container">
        <div class="section-header" id="why-choose-us">
          <h2 class="section-title text-center">{{ $t('home.whyChooseTitle') }}</h2>
          <p class="section-subtitle text-center">{{ $t('home.whyChooseSubtitle') }}</p>
        </div>

        <div class="features-grid">
          <div class="feature-card card">
            <div class="feature-icon">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M33.3333 18.3333V10C33.3333 8.15905 31.8409 6.66667 30 6.66667H10C8.15905 6.66667 6.66667 8.15905 6.66667 10V30C6.66667 31.841 8.15905 33.3333 10 33.3333H18.3333" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M26.6667 21.6667L30.0001 25.0001L36.6667 18.3334" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M13.3333 16.6667H26.6667" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M13.3333 23.3333H18.3333" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <h3 class="feature-title">{{ $t('home.features.instant.title') }}</h3>
            <p class="feature-description">{{ $t('home.features.instant.description') }}</p>
          </div>

          <div class="feature-card card">
            <div class="feature-icon">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 36.6667C29.2048 36.6667 36.6667 29.2048 36.6667 20C36.6667 10.7953 29.2048 3.33334 20 3.33334C10.7953 3.33334 3.33334 10.7953 3.33334 20C3.33334 29.2048 10.7953 36.6667 20 36.6667Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M20 13.3333V20L25 25" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <h3 class="feature-title">{{ $t('home.features.allTime.title') }}</h3>
            <p class="feature-description">{{ $t('home.features.allTime.description') }}</p>
          </div>

          <div class="feature-card card">
            <div class="feature-icon">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M33.3333 18.3333V10C33.3333 8.15905 31.8409 6.66667 30 6.66667H10C8.15905 6.66667 6.66667 8.15905 6.66667 10V30C6.66667 31.841 8.15905 33.3333 10 33.3333H18.3333" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M20 20L36.6667 36.6667" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M36.6667 20V36.6667H20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <h3 class="feature-title">{{ $t('home.features.privacy.title') }}</h3>
            <p class="feature-description">{{ $t('home.features.privacy.description') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section class="section how-it-works-section">
      <div class="container">
        <h2 class="section-title text-center">{{ $t('home.howToUseTitle') }}</h2>
        <p class="section-subtitle text-center">{{ $t('home.howToUseSubtitle') }}</p>

        <div class="steps-container">
          <div class="step-item">
            <div class="step-number">1</div>
            <h3 class="step-title">{{ $t('home.steps.select.title') }}</h3>
            <p class="step-description">{{ $t('home.steps.select.description') }}</p>
          </div>

          <div class="step-divider"></div>

          <div class="step-item">
            <div class="step-number">2</div>
            <h3 class="step-title">{{ $t('home.steps.use.title') }}</h3>
            <p class="step-description">{{ $t('home.steps.use.description') }}</p>
          </div>

          <div class="step-divider"></div>

          <div class="step-item">
            <div class="step-number">3</div>
            <h3 class="step-title">{{ $t('home.steps.receive.title') }}</h3>
            <p class="step-description">{{ $t('home.steps.receive.description') }}</p>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
export default {
  name: 'HomePage'
}
</script>

<style scoped>
.hero-section {
  padding: 120px 0 80px;
  background-color: var(--background-color);
  position: relative;
  overflow: hidden;
}

.hero-section:before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(68, 97, 242, 0.05) 0%, rgba(68, 97, 242, 0) 70%);
  z-index: 0;
}

.hero-section:after {
  content: '';
  position: absolute;
  bottom: -50%;
  left: -50%;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(56, 191, 255, 0.05) 0%, rgba(56, 191, 255, 0) 70%);
  z-index: 0;
}

.hero-content {
  max-width: 600px;
  margin-right: 40px;
  position: relative;
  z-index: 1;
}

.hero-title {
  font-size: 48px;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 24px;
  color: var(--text-primary);
}

.hero-subtitle {
  font-size: 18px;
  line-height: 1.6;
  margin-bottom: 32px;
  color: var(--text-secondary);
}

.hero-actions {
  display: flex;
  gap: 16px;
}

.hero-image {
  max-width: 500px;
  position: relative;
  z-index: 1;
}

.hero-image img {
  width: 100%;
  height: auto;
}

.container {
  display: flex;
  align-items: center;
  justify-content: space-between;
}


.black-friday-section {
  padding: 0 0 48px;
  background-color: var(--background-color);
  position: relative;
  overflow: hidden;
}

.black-friday-section .container {
  display: block;
}

.black-friday-card {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 32px;
  padding: 34px;
  overflow: hidden;
  border-radius: 28px;
  border: 1px solid rgba(255, 204, 102, 0.45);
  background:
    radial-gradient(circle at 15% 20%, rgba(255, 204, 102, 0.28), transparent 30%),
    radial-gradient(circle at 85% 10%, rgba(255, 57, 57, 0.22), transparent 28%),
    linear-gradient(135deg, #090909 0%, #17100d 48%, #2b0909 100%);
  box-shadow: 0 22px 46px rgba(5, 5, 5, 0.32), inset 0 0 0 1px rgba(255, 255, 255, 0.06);
}

.black-friday-card:before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(120deg, transparent 0%, rgba(255, 255, 255, 0.09) 45%, transparent 70%);
  pointer-events: none;
}

.black-friday-main,
.black-friday-offer {
  position: relative;
  z-index: 1;
}

.black-friday-main {
  flex: 1;
  min-width: 0;
}

.black-friday-badge {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  margin-bottom: 16px;
  border-radius: 999px;
  color: #ffd56a;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  background: rgba(255, 213, 106, 0.12);
  border: 1px solid rgba(255, 213, 106, 0.36);
}

.black-friday-title {
  margin: 0 0 12px;
  color: #fff8e6;
  font-size: 32px;
  font-weight: 800;
  line-height: 1.25;
}

.black-friday-subtitle {
  max-width: 680px;
  margin: 0 0 20px;
  color: rgba(255, 248, 230, 0.78);
  font-size: 16px;
  line-height: 1.7;
}

.black-friday-highlights {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.black-friday-highlights span {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 12px;
  color: #ffe0a3;
  font-size: 13px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 213, 106, 0.2);
}

.black-friday-offer {
  width: 280px;
  flex-shrink: 0;
  padding: 24px;
  border-radius: 22px;
  background: rgba(10, 10, 10, 0.58);
  border: 1px solid rgba(255, 213, 106, 0.28);
  box-shadow: inset 0 0 26px rgba(255, 213, 106, 0.08);
}

.offer-label {
  margin-bottom: 8px;
  color: #ff6b4a;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.offer-price-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 20px;
}

.offer-price {
  color: #ffd56a;
  font-size: 24px;
  font-weight: 800;
}

.offer-original {
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  text-decoration: line-through;
}

.black-friday-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 44px;
  border-radius: 14px;
  color: #1b0f00;
  font-weight: 800;
  text-decoration: none;
  background: linear-gradient(135deg, #ffd56a 0%, #ff9f1a 48%, #ff3d2e 100%);
  box-shadow: 0 12px 24px rgba(255, 87, 34, 0.32);
  transition: transform 0.2s, box-shadow 0.2s;
}

.black-friday-cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 30px rgba(255, 87, 34, 0.42);
}

.features-section {
  background-color: var(--card-background);
  border-top: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-top: 60px;
}

.feature-card {
  padding: 30px;
  text-align: center;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  border: 1px solid var(--border-color);
  overflow: hidden;
  z-index: 1;
  transition: all 0.3s;
}

.feature-card:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, #4461F2 0%, #38BFFF 100%);
  transition: all 0.3s;
  z-index: -1;
}

.feature-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 16px 30px rgba(15, 23, 42, 0.1);
  border-color: rgba(68, 97, 242, 0.1);
}

.feature-card:hover:before {
  height: 100%;
  opacity: 0.05;
}

.feature-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: var(--hover-color);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  color: var(--primary-color);
}

.feature-title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 16px;
  color: var(--text-primary);
}

.feature-description {
  color: var(--text-secondary);
  line-height: 1.6;
}

.how-it-works-section {
  background-color: var(--background-color);
}

.steps-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 60px;
  position: relative;
}

.step-item {
  flex: 1;
  text-align: center;
  padding: 0 20px;
  position: relative;
  z-index: 2;
}

.step-number {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4461F2 0%, #38BFFF 100%);
  color: white;
  font-size: 24px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  position: relative;
  box-shadow: 0 8px 16px rgba(68, 97, 242, 0.25);
  z-index: 1;
}

.step-number:after {
  content: '';
  position: absolute;
  top: -5px;
  left: -5px;
  right: -5px;
  bottom: -5px;
  background: linear-gradient(135deg, #4461F2 0%, #38BFFF 100%);
  border-radius: 50%;
  z-index: -1;
  opacity: 0.3;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.3;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.15;
  }
  100% {
    transform: scale(1);
    opacity: 0.3;
  }
}

.step-title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 16px;
  color: var(--text-primary);
}

.step-description {
  color: var(--text-secondary);
  line-height: 1.6;
}

.step-divider {
  height: 2px;
  background-color: var(--border-color);
  flex: 1;
  margin-top: 30px;
  position: relative;
  z-index: 1;
}

.cta-section {
  background-color: var(--background-color);
}

.cta-card {
  background-color: var(--card-background);
  border-radius: var(--radius-lg);
  padding: 60px;
  box-shadow: var(--shadow-lg);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid var(--border-color);
}

.cta-content {
  max-width: 600px;
}

.cta-title {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 16px;
  color: var(--text-primary);
}

.cta-text {
  font-size: 18px;
  color: var(--text-secondary);
}

.cta-actions {
  display: flex;
  gap: 16px;
}

@media (max-width: 1024px) {
  .hero-section {
    padding: 100px 0 60px;
  }

  .hero-title {
    font-size: 40px;
  }

  .features-grid {
    gap: 20px;
  }

  .cta-card {
    padding: 40px;
  }
}

@media (max-width: 768px) {
  .container {
    flex-direction: column;
  }

  .hero-content {
    margin-right: 0;
    margin-bottom: 40px;
    text-align: center;
  }

  .hero-actions {
    justify-content: center;
  }

  .black-friday-card {
    flex-direction: column;
    gap: 24px;
    padding: 28px;
  }

  .black-friday-offer {
    width: 100%;
  }

  .features-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  .steps-container {
    flex-direction: column;
    gap: 40px;
  }

  .step-divider {
    width: 2px;
    height: 40px;
    margin: 0 auto;
  }

  .cta-card {
    flex-direction: column;
    text-align: center;
    padding: 30px;
  }

  .cta-content {
    margin-bottom: 30px;
  }

  .cta-actions {
    flex-direction: column;
    width: 100%;
  }
}

@media (max-width: 480px) {
  .hero-section {
    padding: 80px 0 40px;
  }

  .container {
    padding: 0 16px;
  }

  .hero-title {
    font-size: 28px;
    line-height: 1.3;
    margin-bottom: 16px;
  }

  .hero-subtitle {
    font-size: 16px;
    margin-bottom: 24px;
  }

  .hero-actions {
    flex-direction: column;
    gap: 12px;
    width: 100%;
  }

  .hero-actions .btn {
    width: 100%;
    padding: 14px 20px;
    font-size: 16px;
    text-align: center;
    border-radius: 8px;
  }

  .hero-image {
    max-width: 100%;
    margin-top: 20px;
  }

  .black-friday-section {
    padding: 0 0 32px;
  }

  .black-friday-card {
    padding: 22px;
    border-radius: 22px;
  }

  .black-friday-title {
    font-size: 24px;
  }

  .black-friday-subtitle {
    font-size: 14px;
  }

  .black-friday-highlights {
    flex-direction: column;
  }

  .black-friday-offer {
    padding: 18px;
  }

  .offer-price {
    font-size: 20px;
  }

  .section {
    padding: 40px 0;
  }

  .section-title {
    font-size: 24px;
    margin-bottom: 20px;
  }

  .section-subtitle {
    font-size: 16px;
    margin-bottom: 30px;
    padding: 0 10px;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 20px;
    margin-top: 40px;
  }

  .feature-card {
    padding: 24px 20px;
  }

  .feature-icon {
    width: 60px;
    height: 60px;
    margin-bottom: 16px;
  }

  .feature-icon svg {
    width: 32px;
    height: 32px;
  }

  .feature-title {
    font-size: 18px;
    margin-bottom: 12px;
  }

  .feature-description {
    font-size: 14px;
    line-height: 1.5;
  }

  .steps-container {
    margin-top: 40px;
  }

  .step-item {
    padding: 0 10px;
  }

  .step-number {
    width: 50px;
    height: 50px;
    font-size: 20px;
    margin-bottom: 16px;
  }

  .step-title {
    font-size: 18px;
    margin-bottom: 12px;
  }

  .step-description {
    font-size: 14px;
    line-height: 1.5;
  }

  .step-divider {
    height: 30px;
    width: 2px;
  }

  .cta-card {
    padding: 24px 20px;
    margin: 0 16px;
  }

  .cta-title {
    font-size: 24px;
    margin-bottom: 12px;
  }

  .cta-text {
    font-size: 16px;
    margin-bottom: 20px;
  }

  .cta-actions .btn {
    width: 100%;
    padding: 14px 20px;
    font-size: 16px;
    border-radius: 8px;
  }
}

/* 针对小屏幕设备的额外优化 */
@media (max-width: 360px) {
  .container {
    padding: 0 12px;
  }

  .hero-title {
    font-size: 24px;
  }

  .hero-subtitle {
    font-size: 14px;
  }

  .section-title {
    font-size: 20px;
  }

  .feature-card {
    padding: 20px 16px;
  }

  .cta-card {
    padding: 20px 16px;
  }
}
</style>
